import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "sonner";
import { AppProvider } from "./context/AppContext";
import { Shell } from "./components/layout/Shell";

// Pages
import Dashboard from "./pages/Dashboard/Dashboard";
import CaseManagement from "./pages/CaseManagement/CaseManagement";
import CaseDetail from "./pages/CaseManagement/CaseDetail";
import CaseDeletionRequests from "./pages/CaseManagement/CaseDeletionRequests";
import ImportExport from "./pages/ImportExport/ImportExport";
import FormBuilder from "./pages/FormBuilder/FormBuilder";
import QCDashboard from "./pages/QCDashboard/QCDashboard";
import UserManagement from "./pages/UserManagement/UserManagement";
import ClientManagement from "./pages/ClientManagement/ClientManagement";
import ProductRates from "./pages/ProductRates/ProductRates";
import PenaltyManagement from "./pages/PenaltyManagement/PenaltyManagement";
import InvoicePayments from "./pages/InvoicePayments/InvoicePayments";
import Notifications from "./pages/Notifications/Notifications";
import Reports from "./pages/Reports/Reports";
import Analytics from "./pages/Reports/Analytics";
import DownloadReports from "./pages/Reports/DownloadReports";
import TeamPerformance from "./pages/UserManagement/TeamPerformance";
import AccessControl from "./pages/AccessControl/AccessControl";
import StatusTrackerPage from "./pages/StatusTracker/StatusTracker";
import ManualGeoTagger from "./pages/StatusTracker/ManualGeoTagger";
import PayoutRequests from "./pages/PayoutRequests/PayoutRequests";
import RemovedCases from "./pages/RemovedCases/RemovedCases";
import ExtraCosts from "./pages/ExtraCosts/ExtraCosts";
import VendorModule from "./pages/VendorModule/VendorModule";
import ProductVerificationManagement from "./pages/ProductVerification/ProductVerificationManagement";
import ManualVerification from "./pages/ManualVerification/ManualVerification";
import BusinessPartner from "./pages/BusinessPartner/BusinessPartner";
import IDAndDocs from "./pages/IDAndDocs/IDAndDocs";
import CaseHistory from "./pages/CaseManagement/CaseHistory";

function TitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    const routeTitles = {
      "/": "Dashboard",
      "/cases": "Case Management",
      "/import-export": "Import/Export",
      "/form-builder": "Form Builder",
      "/qc-dashboard": "QC Dashboard",
      "/users": "User Management",
      "/clients": "Client Management",
      "/products": "Products & Rates",
      "/penalties": "Penalty Management",
      "/invoices": "Invoices & Payments",
      "/notifications": "Notifications",
      "/reports": "Reports & Analytics",
      "/analytics": "Analytics Dashboard",
      "/team-performance": "Team Performance",
      "/download-reports": "Download Reports Center",
      "/access-control": "Access Control",
      "/status-tracker": "Status Tracker",
      "/geo-tagger": "Manual Geo Tagger",
      "/payouts": "Payout Requests",
      "/removed-cases": "Removed Cases",
      "/extra-costs": "Extra Cost Approvals",
      "/vendor": "Vendor Hub",
      "/product-verification": "Product & Verification",
      "/deletion-requests": "Deletion Requests",
    };

    let title = "Ad Ventures";
    
    // Check if it's a dynamic route like /cases/:id
    if (location.pathname.endsWith('/history')) {
        title = "Case History | Ad Ventures";
    } else if (location.pathname.startsWith('/cases/') && location.pathname !== '/cases') {
        title = "Case Detail | Ad Ventures";
    } else if (location.pathname.startsWith('/manual-verification')) {
        title = "Manual Verification Portal | Ad Ventures";
    } else if (location.pathname.startsWith('/users')) {
        title = "User Management | Ad Ventures";
    } else {
        const pageTitle = routeTitles[location.pathname];
        if (pageTitle) {
            title = `${pageTitle} | Ad Ventures`;
        }
    }

    document.title = title;
  }, [location]);

  return null;
}

import { useApp } from "./context/AppContext";
import Login from "./pages/Auth/Login";

import FEModule from "./pages/FEModule/FEModule";

function AppRoutes() {
  const { isAuthenticated } = useApp();
  const location = useLocation();

  if (!isAuthenticated) {
    if (location.pathname && location.pathname !== '/' && location.pathname !== '/login') {
      sessionStorage.setItem('redirect_after_login', location.pathname + location.search);
    }
    return (
      <Routes>
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/cases" element={<CaseManagement />} />
        <Route path="/cases/:id" element={<CaseDetail />} />
        <Route path="/cases/:id/history" element={<CaseHistory />} />
        <Route path="/import-export" element={<ImportExport />} />
        <Route path="/form-builder" element={<FormBuilder />} />
        <Route path="/qc-dashboard" element={<QCDashboard />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/users/:submenu" element={<UserManagement />} />
        <Route path="/clients" element={<ClientManagement />} />
        <Route path="/products" element={<ProductRates />} />
        <Route path="/penalties" element={<PenaltyManagement />} />
        <Route path="/invoices" element={<InvoicePayments />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/reports" element={<Reports />} />
        <Route path="/analytics" element={<Analytics />} />
        <Route path="/download-reports" element={<DownloadReports />} />
        <Route path="/team-performance" element={<TeamPerformance />} />
        <Route path="/access-control" element={<AccessControl />} />
        <Route path="/status-tracker" element={<StatusTrackerPage />} />
        <Route path="/geo-tagger" element={<ManualGeoTagger />} />
        <Route path="/payouts" element={<PayoutRequests />} />
        <Route path="/removed-cases" element={<RemovedCases />} />
        <Route path="/deletion-requests" element={<CaseDeletionRequests />} />
        <Route path="/extra-costs" element={<ExtraCosts />} />
        <Route path="/vendor" element={<VendorModule />} />
        <Route path="/product-verification" element={<ProductVerificationManagement />} />
        <Route path="/manual-verification/:statusGroup" element={<ManualVerification />} />
        <Route path="/fe-dashboard" element={<FEModule />} />
        <Route path="/bp-dashboard" element={<BusinessPartner />} />
        <Route path="/id-auth" element={<IDAndDocs />} />
      </Routes>
    </Shell>
  );
}

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <TitleUpdater />
        <AppRoutes />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#ffffff',
              border: '1px solid #e2e8f0',
              color: '#0f172a',
            },
          }}
        />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
